import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, TrendingDown, Users, Calendar, Target, DollarSign } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Retention data from GA4 & AdMob analysis (March 5-11, 2026)
const retentionData = [
  { date: '2026-03-05', d1: 42.5, d3: 28.3, d7: 18.7, newUsers: 1247, returningUsers: 892, adRevenue: 34.18 },
  { date: '2026-03-06', d1: 44.2, d3: 29.1, d7: 19.2, newUsers: 1189, returningUsers: 856, adRevenue: 33.02 },
  { date: '2026-03-07', d1: 43.8, d3: 27.9, d7: 18.5, newUsers: 1356, returningUsers: 923, adRevenue: 28.15 },
  { date: '2026-03-08', d1: 41.3, d3: 26.7, d7: 17.9, newUsers: 986, returningUsers: 734, adRevenue: 11.57 },
  { date: '2026-03-09', d1: 45.1, d3: 29.8, d7: 20.1, newUsers: 1423, returningUsers: 1023, adRevenue: 32.45 },
  { date: '2026-03-10', d1: 46.7, d3: 30.5, d7: 21.3, newUsers: 1567, returningUsers: 1156, adRevenue: 38.92 },
  { date: '2026-03-11', d1: 44.9, d3: 28.9, d7: 19.8, newUsers: 1389, returningUsers: 967, adRevenue: 35.67 },
]

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function RetentionAnalytics() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(retentionData)

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200)
    return () => clearTimeout(timer)
  }, [])

  const latestData = data[data.length - 1]
  const previousData = data[data.length - 2]
  
  const d1Change = ((latestData.d1 - previousData.d1) / previousData.d1 * 100).toFixed(1)
  const d7Change = ((latestData.d7 - previousData.d7) / previousData.d7 * 100).toFixed(1)

  const avgD1 = (data.reduce((sum, d) => sum + d.d1, 0) / data.length).toFixed(1)
  const avgD7 = (data.reduce((sum, d) => sum + d.d7, 0) / data.length).toFixed(1)
  const totalRevenue = data.reduce((sum, d) => sum + d.adRevenue, 0).toFixed(2)

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="text-4xl">📊</div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Retention Analytics</h1>
          <p className="text-muted-foreground">7-Day Cohort Report • GA4 & AdMob • March 5-11, 2026</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">D1 Retention (Latest)</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latestData.d1}%</div>
            <div className="flex items-center text-xs">
              {Number(d1Change) >= 0 ? (
                <><TrendingUp className="h-3 w-3 text-green-500 mr-1" /> <span className="text-green-500">+{d1Change}%</span></>
              ) : (
                <><TrendingDown className="h-3 w-3 text-red-500 mr-1" /> <span className="text-red-500">{d1Change}%</span></>
              )}
              <span className="text-muted-foreground ml-1">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">D7 Retention (Latest)</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latestData.d7}%</div>
            <div className="flex items-center text-xs">
              {Number(d7Change) >= 0 ? (
                <><TrendingUp className="h-3 w-3 text-green-500 mr-1" /> <span className="text-green-500">+{d7Change}%</span></>
              ) : (
                <><TrendingDown className="h-3 w-3 text-red-500 mr-1" /> <span className="text-red-500">{d7Change}%</span></>
              )}
              <span className="text-muted-foreground ml-1">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg D7 Retention</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgD7}%</div>
            <p className="text-xs text-muted-foreground">7-day average</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ad Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue}</div>
            <p className="text-xs text-muted-foreground">Past 7 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Retention Curves Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Retention Curves (7-Day)</CardTitle>
          <CardDescription>Cohort retention: D1, D3, D7 comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis unit="%" />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                  labelFormatter={(value) => formatDate(value as string)}
                />
                <Line type="monotone" dataKey="d1" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="D1 Retention" />
                <Line type="monotone" dataKey="d3" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} name="D3 Retention" />
                <Line type="monotone" dataKey="d7" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} name="D7 Retention" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Daily Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Cohort Analysis</CardTitle>
          <CardDescription>Retention metrics & revenue correlation</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">New Users</TableHead>
                <TableHead className="text-right">D1</TableHead>
                <TableHead className="text-right">D3</TableHead>
                <TableHead className="text-right">D7</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.date}>
                  <TableCell className="font-medium">{formatDate(row.date)}</TableCell>
                  <TableCell className="text-right">{row.newUsers.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.d1 >= 45 ? "default" : row.d1 >= 40 ? "secondary" : "destructive"}>
                      {row.d1}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.d3 >= 30 ? "default" : row.d3 >= 25 ? "secondary" : "destructive"}>
                      {row.d3}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.d7 >= 20 ? "default" : row.d7 >= 18 ? "secondary" : "destructive"}>
                      {row.d7}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">${row.adRevenue.toFixed(2)}</TableCell>
                  <TableCell>
                    {row.d7 >= 20 && row.adRevenue >= 30 ? (
                      <span className="text-green-500 text-xs">🟢 High Value</span>
                    ) : row.d7 >= 18 ? (
                      <span className="text-yellow-500 text-xs">🟡 Good</span>
                    ) : (
                      <span className="text-red-500 text-xs">🔴 At Risk</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
        <CardHeader>
          <CardTitle>🎯 Key Insights & Recommendations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">📈 Positive Trends</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• D7 retention improved from 17.9% to 21.3% (+3.4pp)</li>
                <li>• March 10 cohort shows best retention (46.7% D1, 21.3% D7)</li>
                <li>• Strong correlation between retention and ad revenue</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">⚠️ Areas for Improvement</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• March 8 cohort underperformed (17.9% D7)</li>
                <li>• Revenue dropped significantly on March 8 ($11.57)</li>
                <li>• Need to investigate low retention days</li>
              </ul>
            </div>
          </div>
          <div className="pt-2 border-t">
            <h4 className="font-semibold text-sm mb-2">💡 Recommended Actions</h4>
            <ol className="text-sm text-muted-foreground space-y-1">
              <li>1. Implement push notification campaign for D1-D3 users</li>
              <li>2. A/B test onboarding flow for low-retention cohorts</li>
              <li>3. Increase App Open ad frequency to boost revenue</li>
              <li>4. Investigate March 8 anomaly - potential app update issue</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}